import { createContext, useContext, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { ApiError, getMe, hasServiceToken, qk, type AuthMembership, type AuthUser } from '../api.js';
import { effectiveRole, LOGIN_PATH, type Role } from '../lib/auth.js';
import Spinner from './Spinner.js';

/**
 * §27.7 — Route guard + oturum bağlamı.
 *
 * "Uygulama açılışında `GET /api/auth/me`; 401 → `/giris`'e yönlendir." Guard bir
 * ROTA KABUĞUDUR: `/giris` ve `/davet` onun DIŞINDA kalır (§27.5 muafiyet listesinin
 * panel karşılığı) — içeride olsalardı 401 → /giris → guard → 401 döngüsü oluşurdu.
 *
 * Aynı sorgu oturum bilgisini de taşır; avatar menüsü ve "Ayarlar → Üyeler" rolü
 * buradan okur, ikinci kez `me` çağırmaz.
 */

export interface AuthState {
  /** Oturum sahibi; servis belirtecinde sunucu kullanıcı DÖNMEYEBİLİR (§27.6). */
  user: AuthUser | null;
  memberships: AuthMembership[];
  /** §27.6 — `Authorization: Bearer <ADMIN_TOKEN>` ile mi geziliyor. */
  serviceToken: boolean;
  /**
   * Sunucu auth uçlarını sunuyor mu (yanıt 200 mü geldi).
   *
   * NEDEN gerekli: `/api/auth/me` 404 veya ağ hatası dönerse bu, "oturum yok" DEĞİL
   * "bu sunucu sürümünde auth yok" demektir. O durumda panel eskisi gibi (yalnız
   * bearer ile) çalışmaya devam eder — §27.6'nın "mevcut e2e'ler çalışmaya devam
   * eder" kuralı yönlendirme yüzünden kırılmamalıdır.
   */
  authAvailable: boolean;
}

const EMPTY_AUTH: AuthState = {
  user: null,
  memberships: [],
  serviceToken: false,
  authAvailable: false,
};

const AuthContext = createContext<AuthState>(EMPTY_AUTH);

/** Bileşenler oturumu buradan okur (sağlayıcı yoksa boş oturum döner — test dostu). */
export function useAuth(): AuthState {
  return useContext(AuthContext);
}

/** §27.6/§27.7 — aktif projedeki efektif rol; karar `lib/auth.ts`'teki saf fonksiyondadır. */
export function useRole(pid: string): Role | null {
  const auth = useAuth();
  return effectiveRole({
    user: auth.user,
    memberships: auth.memberships,
    pid,
    serviceToken: auth.serviceToken,
  });
}

/** Guard'ın ilk karesi — oturum sorulurken panelin iskeletini çizmenin anlamı yok. */
function AuthSplash() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-[#fafafa]">
      <Spinner size="lg" label="Oturum kontrol ediliyor…" />
    </div>
  );
}

export default function AuthGate() {
  const location = useLocation();

  const me = useQuery({
    queryKey: qk.me(),
    queryFn: ({ signal }) => getMe(signal),
    // 401 tekrar denemekle düzelmez; auth ucu yoksa da beklemeye değmez.
    retry: false,
    // §27.3 oturum TTL'i kayar; panelin her odaklanmada kimlik sorması gereksiz.
    staleTime: 5 * 60_000,
  });

  const status = me.error instanceof ApiError ? me.error.status : undefined;

  const value = useMemo<AuthState>(
    () => ({
      user: me.data?.user ?? null,
      memberships: me.data?.memberships ?? [],
      // Sunucunun `service` bayrağı önceliklidir; yoksa token'ın varlığına bakılır (§27.6).
      serviceToken: me.data?.service ?? hasServiceToken(),
      authAvailable: me.isSuccess,
    }),
    [me.data, me.isSuccess],
  );

  if (me.isLoading) return <AuthSplash />;

  /*
   * §27.7 — tek yönlendirme koşulu 401'dir. Nereden geldiğimiz `state.from` ile
   * taşınır; giriş başarılı olunca kullanıcı aynı sayfaya döner (derin bağlantı
   * paylaşımı çalışsın).
   */
  if (status === 401) {
    return (
      <Navigate to={LOGIN_PATH} replace state={{ from: `${location.pathname}${location.search}` }} />
    );
  }

  return (
    <AuthContext.Provider value={value}>
      <Outlet />
    </AuthContext.Provider>
  );
}
